// Shared PDF builder for the whole app.
// Text-based and paginated: sharp, searchable, and never cuts a line in half.
// Replaces pdfDownload.js (image-based). Screens move over one at a time.
//
// Usage:
//   await buildPdf({
//     headerTitle: "Sunnyside Nursing Home",   // big title at the top
//     docTitle: "Resident Wishes Summary",     // subtitle in red
//     subjectLabel: "Resident",                // "Resident: Mary Byrne"
//     subjectName: "Mary Byrne",
//     generatedByName: "Jane Smith",           // optional
//     sections: [{ heading, note?, rows: [[label, value], ...] }],
//     signOff: true,                           // adds Reviewed by / Signature / Date
//     confidentialNote: "Confidential - ...",  // footer text, optional
//     returnBase64: false,                     // true = return data, don't download
//   });

const JSPDF_SRC = "https://cdn.jsdelivr.net/npm/jspdf@2.5.1/dist/jspdf.umd.min.js";

const C = {
  navy: [0, 51, 83],
  red: [152, 0, 0],
  steel: [101, 120, 153],
  text: [30, 30, 30],
  muted: [110, 110, 110],
  rule: [220, 222, 228],
};

function loadScript(src) {
  return new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = src;
    s.async = true;
    s.onload = resolve;
    s.onerror = () => reject(new Error("Could not load the PDF library"));
    document.head.appendChild(s);
  });
}

async function ensureJsPdf() {
  if (!window.jspdf) await loadScript(JSPDF_SRC);
  return window.jspdf.jsPDF;
}

function slug(s, fallback) {
  return String(s || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "") || fallback;
}

export async function buildPdf(opts = {}) {
  const {
    headerTitle = "LastWishBox",
    docTitle = "",
    subjectLabel = "Name",
    subjectName = "",
    generatedByName = "",
    generatedAtMs = Date.now(),
    sections = [],
    signOff = false,
    confidentialNote = "",
    fileNamePrefix = "",
    returnBase64 = false,
  } = opts;

  const JsPDF = await ensureJsPdf();
  const doc = new JsPDF({ unit: "pt", format: "a4" });

  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const M = 48;
  const bottomLimit = pageH - 64;
  const labelW = 150;
  const valueX = M + labelW + 14;
  const valueW = pageW - M - valueX;
  const lineH = 13;

  let y = M;

  const ink = (rgb) => doc.setTextColor(rgb[0], rgb[1], rgb[2]);
  const pen = (rgb, w) => { doc.setDrawColor(rgb[0], rgb[1], rgb[2]); doc.setLineWidth(w); };
  const font = (style, size, rgb) => { doc.setFont("helvetica", style); doc.setFontSize(size); ink(rgb); };

  const runningHead = [docTitle, subjectName].filter(Boolean).join(" - ");

  const newPage = () => {
    doc.addPage();
    y = M;
    if (runningHead) {
      font("normal", 8, C.muted);
      doc.text(runningHead, M, y);
      y += 22;
    }
  };

  const ensureSpace = (h) => {
    if (y + h > bottomLimit) newPage();
  };

  // Header
  font("bold", 18, C.navy);
  doc.text(headerTitle || "LastWishBox", M, y + 6);
  y += 30;
  if (docTitle) {
    font("bold", 13, C.red);
    doc.text(docTitle, M, y);
    y += 22;
  }
  if (subjectName) {
    font("normal", 11, C.text);
    doc.text(`${subjectLabel}: ${subjectName}`, M, y);
    y += 16;
  }
  const when = new Date(generatedAtMs).toLocaleString("en-IE", { dateStyle: "long", timeStyle: "short" });
  font("normal", 9, C.muted);
  doc.text(`Generated ${when}${generatedByName ? ` by ${generatedByName}` : ""}`, M, y);
  y += 14;
  pen(C.rule, 1);
  doc.line(M, y, pageW - M, y);
  y += 24;

  // Sections
  sections.forEach((section) => {
    // keepTogether: start a new page rather than split a short section across
    // two. Ignored if the section is taller than a page on its own.
    if (section.keepTogether) {
      const rowCount = (section.rows || []).length;
      const estimate = 56 + rowCount * (lineH + 12) + (section.note ? 24 : 0);
      if (estimate < bottomLimit - M) ensureSpace(estimate);
      else ensureSpace(56);
    } else {
      ensureSpace(56);
    }
    font("bold", 12, C.navy);
    doc.text(section.heading || "", M, y);
    y += 6;
    pen(C.red, 1.5);
    doc.line(M, y, M + 40, y);
    y += 18;

    if (section.note) {
      font("italic", 9, C.muted);
      const noteLines = doc.splitTextToSize(String(section.note), pageW - M * 2);
      ensureSpace(noteLines.length * 12);
      doc.text(noteLines, M, y);
      y += noteLines.length * 12 + 6;
    }

    (section.rows || []).forEach((row) => {
      const label = row[0];
      const value = row[1];
      const empty = value == null || String(value).trim() === "";
      const shown = empty ? "Not recorded" : String(value);
      const valueStyle = empty ? "italic" : "normal";
      const valueInk = empty ? C.muted : C.text;

      font("bold", 9, C.steel);
      const labelLines = doc.splitTextToSize(String(label || ""), labelW);
      font(valueStyle, 10, valueInk);
      const valueLines = doc.splitTextToSize(shown, valueW);

      ensureSpace(Math.min(Math.max(labelLines.length, valueLines.length), 3) * lineH);

      const startY = y;
      font("bold", 9, C.steel);
      doc.text(labelLines, M, y);

      font(valueStyle, 10, valueInk);
      let vy = y;
      let broke = false;
      valueLines.forEach((line) => {
        if (vy + lineH > bottomLimit) {
          newPage();
          vy = y;
          broke = true;
          font(valueStyle, 10, valueInk);
        }
        doc.text(line, valueX, vy);
        vy += lineH;
      });

      y = broke ? vy : Math.max(vy, startY + labelLines.length * lineH);
      pen(C.rule, 0.5);
      doc.line(M, y - 4, pageW - M, y - 4);
      y += 8;
    });

    y += 12;
  });

  // Optional sign-off
  if (signOff) {
    ensureSpace(90);
    y += 8;
    font("bold", 11, C.navy);
    doc.text("Review sign-off", M, y);
    y += 30;
    const colW = (pageW - M * 2) / 3;
    ["Reviewed by", "Signature", "Date"].forEach((lbl, i) => {
      const x = M + i * colW;
      pen(C.text, 0.6);
      doc.line(x, y, x + colW - 18, y);
      font("normal", 8, C.muted);
      doc.text(lbl, x, y + 12);
    });
  }

  // Footer on every page
  const total = doc.getNumberOfPages();
  for (let i = 1; i <= total; i += 1) {
    doc.setPage(i);
    pen(C.rule, 0.5);
    doc.line(M, pageH - 46, pageW - M, pageH - 46);
    font("normal", 7.5, C.muted);
    if (confidentialNote) doc.text(confidentialNote, M, pageH - 34);
    doc.text(`Page ${i} of ${total}`, pageW - M, pageH - 34, { align: "right" });
    doc.text("Produced with LastWishBox", pageW / 2, pageH - 22, { align: "center" });
  }

  if (returnBase64) {
    return { pdfBase64: doc.output("datauristring").split(",")[1] };
  }

  const d = new Date(generatedAtMs);
  const stamp = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  const prefix = fileNamePrefix || slug(docTitle, "document");
  doc.save(`${prefix}-${slug(subjectName, "export")}-${stamp}.pdf`);
  return null;
}
